import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NewsFlashPopup from '../components/NewsFlashPopup';
import SearchScreenNav from '../components/SearchScreenNav';
import { Helmet } from 'react-helmet-async';
import './SearchScreen.css';

// Import the video
import backgroundVideo from '../assets/pridepath video HOME.mp4';

// Custom hook for lazy loading
const useLazyLoad = (options = {}) => {
  const elementRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return { elementRef, isLoaded, isInView, setIsLoaded };
};

// Lazy Video Component
const LazyVideo = ({ 
  src, 
  className = '', 
  wrapperClassName = '',
  eager = false,
  onLoad,
  ...props 
}) => {
  const { elementRef, isLoaded, isInView, setIsLoaded } = useLazyLoad();
  const [videoSrc, setVideoSrc] = useState(eager ? src : '');
  const videoRef = useRef(null);

  useEffect(() => {
    if (eager || isInView) {
      setVideoSrc(src);
    }
  }, [eager, isInView, src]);

  const handleLoadedData = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
    // Start playing the video once it's loaded
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log('Video autoplay failed:', error);
      });
    }
  };

  return (
    <div ref={elementRef} className={`lazy-video-wrapper ${wrapperClassName}`}>
      {!isLoaded && (
        <div className="video-placeholder">
          <div className="video-skeleton-loader" />
        </div>
      )}
      
      {videoSrc && (
        <video
          ref={videoRef}
          className={`lazy-video ${className} ${isLoaded ? 'loaded' : 'loading'}`}
          onLoadedData={handleLoadedData}
          autoPlay
          loop
          muted
          playsInline
          {...props}
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

// Use environment variable for API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pridepath-webapp.onrender.com';

/**
 * SearchScreen Component
 *
 * This is the initial screen of the LGBTQ+ Protections application where users
 * enter their ZIP code and select preferences before proceeding to search for
 * protections and resources in their area.
 * 
 * The component collects:
 * - ZIP code (required, validated)
 * - Population categories (Under 10,000, 10,000-50,000, etc. - multiple selection)
 * - Age verification (Yes/No radio buttons)
 * - Relevant protection categories (Sexual Orientation, Gender Identity, or Both)
 * - Tie-breaker preference (Resources, Rent, or Distance)
 *
 * This data is then passed to the SearchOptions screen for further filtering.
 */

const SearchScreen = () => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for video loading
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // State for ZIP code input and validation
  const [zipCode, setZipCode] = useState('');
  const [zipError, setZipError] = useState('');
  
  // State for population categories (multiple selection)
  const [populationCategories, setPopulationCategories] = useState({
    under10k: false,
    from10kTo50k: false,
    from50kTo200k: false,
    from200kTo1m: false,
    over1m: false
  });
  
  // State for age verification (Yes/No radio buttons)
  const [ageStatus, setAgeStatus] = useState('');
  
  // State for protection categories - now using a single selection
  const [protectionType, setProtectionType] = useState(''); // 'sexual', 'gender', or 'both'
  
  // State for tie breaker preference
  const [tieBreaker, setTieBreaker] = useState(''); // 'resources', 'rent', or 'distance'
  
  // State for page transition animation
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // State for ZIP validation loading
  const [isValidating, setIsValidating] = useState(false);
  
  // State for mobile info section expansion
  const [mobileInfoExpanded, setMobileInfoExpanded] = useState(false);

  useEffect(() => {
    // Smooth scroll to top when component mounts
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  // Clear any stale validation errors on mount and check for autofill
  useEffect(() => {
    // Clear any stale validation errors on mount
    setZipError('');
    
    // If browser autofilled the ZIP, update our state
    const zipInput = document.getElementById('zipCode');
    if (zipInput && zipInput.value && !zipCode) {
      setZipCode(zipInput.value);
    }
  }, []);

  // Handle browser autofill detection and synchronization
  useEffect(() => {
    const handleAutofill = () => {
      const zipInput = document.getElementById('zipCode');
      if (zipInput && zipInput.value !== zipCode) {
        setZipCode(zipInput.value);
        setZipError(''); // Clear any errors when autofill is detected
      }
    };

    // Check for autofill on various browser events
    window.addEventListener('load', handleAutofill);
    document.addEventListener('DOMContentLoaded', handleAutofill);
    
    // Also check periodically for the first few seconds after mount
    // This catches delayed autofill that some browsers do
    const checkInterval = setInterval(handleAutofill, 100);
    const checkTimeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 2000); // Stop checking after 2 seconds
    
    // Also listen for animation events that indicate autofill
    const zipInput = document.getElementById('zipCode');
    if (zipInput) {
      const handleAnimationStart = (e) => {
        if (e.animationName === 'onAutoFillStart') {
          handleAutofill();
        }
      };
      zipInput.addEventListener('animationstart', handleAnimationStart);
      
      // Cleanup
      return () => {
        window.removeEventListener('load', handleAutofill);
        document.removeEventListener('DOMContentLoaded', handleAutofill);
        clearInterval(checkInterval);
        clearTimeout(checkTimeout);
        zipInput.removeEventListener('animationstart', handleAnimationStart);
      };
    }
    
    return () => {
      window.removeEventListener('load', handleAutofill);
      document.removeEventListener('DOMContentLoaded', handleAutofill);
      clearInterval(checkInterval);
      clearTimeout(checkTimeout);
    };
  }, [zipCode]);

  // Check if we're returning from a "no matches" result via page reload
  useEffect(() => {
    // Check sessionStorage for return data
    const returnDataString = sessionStorage.getItem('pridepath_return_data');
    
    if (returnDataString) {
      try {
        const returnData = JSON.parse(returnDataString);
        
        // Pre-fill the ZIP code if provided
        if (returnData.zipCode) {
          setZipCode(returnData.zipCode);
        }
        
        // Pre-fill protection types if provided
        if (returnData.protections) {
          // Convert back to the single selection format
          if (returnData.protections.sexualOrientation && returnData.protections.genderIdentity) {
            setProtectionType('both');
          } else if (returnData.protections.sexualOrientation) {
            setProtectionType('sexual');
          } else if (returnData.protections.genderIdentity) {
            setProtectionType('gender');
          }
        }
        
        // Pre-fill population categories if provided
        if (returnData.populationCategories) {
          setPopulationCategories(returnData.populationCategories);
        }
        
        // Pre-fill tie breaker preference if provided
        if (returnData.tieBreaker) {
          setTieBreaker(returnData.tieBreaker);
        }
        
        // Clear the sessionStorage after using it
        sessionStorage.removeItem('pridepath_return_data');
        
        // Optional: Show a message to the user
        console.log('Welcome back! Your previous selections have been restored.');
        
      } catch (error) {
        console.error('Error parsing return data:', error);
        // Clear corrupted data
        sessionStorage.removeItem('pridepath_return_data');
      }
    }
  }, []); // Empty dependency array - only run once on mount
  
  // Check if we're returning from a "no matches" result with expand population filter flag
  useEffect(() => {
    if (location.state?.expandPopulationFilter) {
      // Pre-fill the ZIP code
      if (location.state.zipCode) {
        setZipCode(location.state.zipCode);
      }
      
      // Select all population categories
      setPopulationCategories({
        under10k: true,
        from10kTo50k: true,
        from50kTo200k: true,
        from200kTo1m: true,
        over1m: true
      });
      
      // Clear the location state to prevent this from running again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Preload video on desktop only for better performance
  useEffect(() => {
    const isDesktop = window.innerWidth >= 1024;
    if (isDesktop) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = backgroundVideo;
      document.head.appendChild(link);

      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, []);
  
  /**
   * Validates the ZIP code format
   * 
   * Checks if the provided string is a valid 5-digit US ZIP code
   * 
   * @param {string} zip - The ZIP code to validate
   * @return {boolean} Whether the ZIP code is valid
   */
  const validateZipCode = (zip) => {
    const zipRegex = /^\d{5}$/;
    return zipRegex.test(zip);
  };
  
  /**
   * Log analytics data
   * 
   * Sends the initial search data to the backend for analytics
   * This is non-blocking and fails silently to not impact user experience
   * 
   * @param {object} data - The analytics data to log
   */
  const logAnalytics = async (data) => {
    try {
      await fetch(`${API_BASE_URL}/api/log-initial-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      // Silently fail - don't block the user
      console.error('Analytics logging failed:', error);
    }
  };
  
  /**
   * Handle protection type radio button changes
   * 
   * Updates the protection type state when a radio button is selected
   * 
   * @param {string} type - The protection type to select ('sexual', 'gender', or 'both')
   */
  const handleProtectionTypeChange = (type) => {
    setProtectionType(type);
  };

  /**
   * Handle tie breaker radio button changes
   * 
   * Updates the tie breaker preference state when a radio button is selected
   * 
   * @param {string} preference - The tie breaker preference ('resources', 'rent', or 'distance')
   */
  const handleTieBreakerChange = (preference) => {
    setTieBreaker(preference);
  };

  /**
   * Handle population category checkbox changes
   * 
   * Updates the population categories state when a checkbox is clicked
   * 
   * @param {string} category - The population category key to toggle
   */
  const handlePopulationCategoryChange = (category) => {
    setPopulationCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  /**
   * Handle age status radio button changes
   * 
   * Updates the age status state when a radio button is selected
   * 
   * @param {string} status - The age status to select ('yes' or 'no')
   */
  const handleAgeStatusChange = (status) => {
    setAgeStatus(status);
  };
  
  /**
   * Helper function to get selected population categories count
   */
  const getSelectedPopulationCount = () => {
    return Object.values(populationCategories).filter(v => v).length;
  };
  
  /**
   * Handle form submission with transition animation
   * 
   * Validates the ZIP code and navigates to the search options screen
   * with the user's selections if validation passes.
   * 
   * @param {Event} e - The form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ZIP code - check if empty
    if (!zipCode) {
      setZipError('Please enter a ZIP code');
      return;
    }
    
    // Validate ZIP code - check format
    if (!validateZipCode(zipCode)) {
      setZipError('Please refreh the page and enter a valid 5-digit ZIP code');
      return;
    }
    
    // Validate protection type selection
    if (!protectionType) {
      // This shouldn't happen due to button being disabled, but just in case
      return;
    }
    
    // Validate tie breaker selection
    if (!tieBreaker) {
      // This shouldn't happen due to button being disabled, but just in case
      return;
    }
    
    // Clear any previous error message
    setZipError('');
    
    // Set validating state
    setIsValidating(true);
    
    // Check if ZIP exists in database
    try {
      const response = await fetch(`${API_BASE_URL}/api/validate-zip/${zipCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setZipError('This ZIP code is not available in our database. Please try another ZIP code.');
          setIsValidating(false);
          return;
        }
        throw new Error('Failed to validate ZIP code');
      }
      
      const data = await response.json();
      
      if (!data.exists) {
        setZipError('This ZIP code is not available in our database. Please try another ZIP code.');
        setIsValidating(false);
        return;
      }
    } catch (error) {
      console.error('Error validating ZIP:', error);
      setZipError('Unable to validate ZIP code. Please try again.');
      setIsValidating(false);
      return;
    }
    
    // After ZIP validation passes
    setIsValidating(false);
    
    // Generate a session ID for this search
    const sessionId = `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Convert protectionType to the expected format
    const protections = {
      sexualOrientation: protectionType === 'sexual' || protectionType === 'both',
      genderIdentity: protectionType === 'gender' || protectionType === 'both'
    };
    
    // Log analytics
    logAnalytics({
      zipCode,
      populationCategories,
      ageStatus,
      protectionType,
      protections,
      tieBreaker,
      sessionId
    });
    
    // Start preloading cities in the background if population filter exists
    const hasPopulationFilter = Object.values(populationCategories).some(v => v);
    if (hasPopulationFilter) {
      // Fire and forget - don't await
      fetch(`${API_BASE_URL}/api/preload-cities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          populationCategories,
          sessionId
        })
      }).catch(error => {
        console.error('Failed to start city preload:', error);
      });
    }
    
    // Log for debugging
    console.log('SearchScreen - Passing to next screen:', {
      zipCode,
      populationCategories,
      ageStatus,
      protections,
      protectionType,
      tieBreaker,
      sessionId
    });
    
    // Start transition animation
    setIsTransitioning(true);
    
    // Navigate with all necessary data
    setTimeout(() => {
      navigate('/search-options', { 
        state: { 
          zipCode, 
          populationCategories,
          ageStatus,
          protections,
          tieBreaker,
          sessionId
        } 
      });
    }, 800); // Match the animation duration
  };

  /**
   * Main component render
   * 
   * Renders a form with ZIP code input, population category checkboxes,
   * age verification radio buttons, protection categories radio buttons,
   * and tie-breaker preference radio buttons.
   */
  return (
    <>
      <Helmet>
        <title>LGBT PridePath | Find LGBTQ+ Friendly Communities</title>
        <meta name="description" content="LGBT PridePath is a free tool to help you find communities with LGBTQ+ legal protections. Search by ZIP code to discover safe, welcoming places based on laws important to you." />
        <link rel="canonical" href={`https://www.lgbtpridepath.org${location.pathname === '/home' ? '/home' : '/'}`} />
      </Helmet>
      
      {/* News Flash Popup - only shows on this screen, once per session */}
      <NewsFlashPopup showOnSearchScreen={true} />
      
      <div className={`search-screen-container ${isTransitioning ? 'transitioning-out' : ''}`}>
        {/* Custom navigation for this screen */}
        <SearchScreenNav />
        
        <div className="wave-wrapper"></div>  
        <div className="extra-waves"></div>
        
        {/* Video container with lazy loading */}
        <div className={`video-side-container ${videoLoaded ? 'video-loaded' : ''}`}>
          <LazyVideo 
            src={backgroundVideo}
            className="side-video"
            eager={false} // Lazy load the video
            onLoad={() => setVideoLoaded(true)}
            wrapperClassName="video-wrapper"
          />
        </div>
        
        {/* Content wrapper for two-column layout */}
        <div className="content-wrapper">
          {/* Left side info section */}
          <div className="info-section">
            {/* <h2>Header and Helpful Info</h2> */}
            <p>
              LGBT PridePath is a free tool designed to help you make an informed decision around relocation, based on the laws and protections that are important to you, or to compare other communities with your own.  Simply input your zip code, mark the legal protections and other factors important to you, and see which communities are closest to you that offer these protections.  This can also include side-by-side comparisons of the area you entered to others that meet your criteria. </p>
            <p>
              All of us should be able to live anywhere we want and feel safe and protected.  Our laws in the United States should protect ALL of us.  But the reality is that some cities and towns simply don't offer us equal protection.
            </p>
            <p>
              The team at the LGBT National Hotline, a trusted non-profit celebrating 30 years of serving the LGBT community, has been working on LGBT PridePath for nearly a year and are proud to offer it as a new tool for you. LGBT PridePath can be a vital tool if you are considering relocation or researching where you can find a community that is more protected and safer for you or your loved ones.  Relocation can be a daunting task. We are here to help.
            </p>
            <p>
              Here at the LGBT National Help Center data integrity is vital. This program has been highly researched and will be updated regularly to ensure accurate and up to date information. 
            </p>    
            <p>
              Additionally, your privacy is our priority. We do not collect, store or share personally identifiable information.
            </p>
            <p>
            </p>
            <p>
            </p>
          </div>
          
          {/* Right side - Mobile Info Section and Search Card */}
          <div className="search-section">
            {/* Mobile Info Section - Shows only on mobile portrait */}
            <div className={`mobile-info-container ${mobileInfoExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="mobile-info-card">
                <div className="mobile-info-header">
                  <div className="mobile-info-title">
                    <h3>About LGBT PridePath</h3>
                  </div>
                </div>
                
                <div className={`mobile-info-body ${mobileInfoExpanded ? 'expanded' : ''}`}>
                  <div className="mobile-info-content">
                    {/* First paragraph - ALWAYS visible */}
                    <div className="mobile-info-intro">
                      <p>
                        LGBT PridePath is a free tool designed to help you make an informed decision around relocation, based on the laws and protections that are important to you, or to compare other communities with your own.  Simply input your zip code, mark the legal protections and other factors important to you, and see which communities are closest to you that offer these protections.  This can also include side-by-side comparisons of the area you entered to others that meet your criteria.
                      </p>
                    </div>

                    {/* Expandable content - Rest of paragraphs */}
                    <div className={`mobile-info-expandable ${mobileInfoExpanded ? 'show' : ''}`}>
                      <p>
                        All of us should be able to live anywhere we want and feel safe and protected.  Our laws in the United States should protect ALL of us.  But the reality is that some cities and towns simply don't offer us equal protection.
                      </p>
                      <p>
                        The team at the LGBT National Hotline, a trusted non-profit celebrating 30 years of serving the LGBT community, has been working on LGBT PridePath for nearly a year and are proud to offer it as a new tool for you. LGBT PridePath can be a vital tool if you are considering relocation or researching where you can find a community that is more protected and safer for you or your loved ones.  Relocation can be a daunting task. We are here to help.
                      </p>
                      <p>
                        Here at the LGBT National Help Center data integrity is vital. This program has been highly researched and will be updated regularly to ensure accurate and up to date information. 
                      </p>

                        <p>
                          <strong>Your Privacy Matters</strong><br />
                          Your privacy is our priority. We do not collect, store or share personally identifiable information.
                        </p>
                    </div>
                  </div>
                  
                  {/* Read More button */}
                  <button 
                    className="expand-button"
                    onClick={() => setMobileInfoExpanded(!mobileInfoExpanded)}
                    aria-label={mobileInfoExpanded ? 'Show less' : 'Read more'}
                  >
                    <span className="button-text">{mobileInfoExpanded ? 'Show less' : 'Read more'}</span>
                    <svg 
                      className={`chevron-icon ${mobileInfoExpanded ? 'rotated' : ''}`}
                      width="16" 
                      height="16" 
                      viewBox="0 0 20 20" 
                      fill="none"
                    >
                      <path 
                        d="M5 7.5L10 12.5L15 7.5" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Search card */}
            <div className="search-card">
              {/* Header section with title and subtitle */}
              <div className="search-header">
                <div className="header-content">
                  <h1>Find LGBTQ+ Protections</h1>
                  <p className="header-subtitle">
                    Discover policies and resources in your area
                  </p>
                </div>
              </div>
              
              <div className="search-body">
                {/* Main form for user input */}
                <form onSubmit={handleSubmit}>
                  {/* ZIP Code input field */}
                  <div className="form-group">
                    <label htmlFor="zipCode">ZIP Code</label>
                    <div className="input-container">
                      <input
                        id="zipCode"
                        type="text"
                        value={zipCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                          setZipCode(value);
                          // Clear error message when user starts typing
                          if (zipError) setZipError('');
                        }}
                        onInput={(e) => {
                          // Handle browser autofill that might not trigger onChange
                          const value = e.target.value.replace(/\D/g, '');
                          if (value !== zipCode) {
                            setZipCode(value);
                            setZipError('');
                          }
                        }}
                        onBlur={(e) => {
                          // Double-check value on blur to catch any edge cases
                          const value = e.target.value.replace(/\D/g, '');
                          if (value !== zipCode) {
                            setZipCode(value);
                          }
                        }}
                        placeholder="e.g. 90210"
                        maxLength="5"
                        className={zipError ? "error" : ""}
                        autoComplete="postal-code"
                      />
                      {/* Location icon inside input */}
                      <div className="input-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    {/* Display validation error message if present */}
                    {zipError && <p className="error-message">{zipError}</p>}
                    <p className="helper-text" style={{ fontSize: '0.9rem' }}>
                      We use this to find local laws and resources
                    </p>
                  </div>
                  
                  {/* Population Category checkboxes */}
                  <div className="form-group population-category-group">
                    <label>
                      Which population sizes would you consider? (Select all that apply)
                      {getSelectedPopulationCount() > 0 && (
                        <span className="selection-count">
                          ({getSelectedPopulationCount()} selected)
                        </span>
                      )}
                    </label>
                    <div className="population-options">
                      {[
                        { key: 'under10k', label: 'Under 10,000' },
                        { key: 'from10kTo50k', label: '10,000 - under 50,000' },
                        { key: 'from50kTo200k', label: '50,000 - under 200,000' },
                        { key: 'from200kTo1m', label: '200,000 - under 1,000,000' },
                        { key: 'over1m', label: '1,000,000 or more' }
                      ].map((category) => (
                        <div 
                          key={category.key} 
                          className="checkbox-group"
                          onClick={() => handlePopulationCategoryChange(category.key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="checkbox-container">
                            <input
                              id={`population-${category.key}`}
                              name={`population-${category.key}`}
                              type="checkbox"
                              checked={populationCategories[category.key]}
                              onChange={() => handlePopulationCategoryChange(category.key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="checkbox-label">
                            <label 
                              htmlFor={`population-${category.key}`}
                              onClick={(e) => e.preventDefault()}
                              style={{ cursor: 'pointer', display: 'block', width: '100%' }}
                            >
                              {category.label}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="helper-text" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      Leave unchecked to see all city sizes
                    </p>
                  </div>
                  
                  {/* Age verification radio buttons */}
                  <div className="form-group age-group">
                    <label>Are you over 18?</label>
                    <div className="age-options">
                      {[
                        { key: 'yes', label: 'Yes' },
                        { key: 'no', label: 'No' }
                      ].map((option) => (
                        <label 
                          key={option.key} 
                          className="radio-checkbox-group"
                          onClick={(e) => {
                            // Prevent double-firing if clicking directly on radio
                            if (e.target.type !== 'radio') {
                              handleAgeStatusChange(option.key);
                            }
                          }}
                        >
                          <div className="radio-checkbox-container">
                            <input
                              id={`age-${option.key}`}
                              name="age-status"
                              type="radio"
                              checked={ageStatus === option.key}
                              onChange={() => handleAgeStatusChange(option.key)}
                              className="radio-checkbox"
                            />
                          </div>
                          <div className="radio-checkbox-label">
                            {option.label}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Protection categories section */}
                  <div className="form-group protections-group">
                    <label>Which protections affect you? <span className="required"></span></label>
                    <div className="protections-options">
                      {/* Sexual Orientation option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleProtectionTypeChange('sexual');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="protection-sexual"
                            name="protection-type"
                            type="radio"
                            checked={protectionType === 'sexual'}
                            onChange={() => handleProtectionTypeChange('sexual')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Sexual Orientation
                        </div>
                      </label>
                      
                      {/* Gender Identity option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleProtectionTypeChange('gender');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="protection-gender"
                            name="protection-type"
                            type="radio"
                            checked={protectionType === 'gender'}
                            onChange={() => handleProtectionTypeChange('gender')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Gender Identity
                        </div>
                      </label>
                      
                      {/* Both option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleProtectionTypeChange('both');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="protection-both"
                            name="protection-type"
                            type="radio"
                            checked={protectionType === 'both'}
                            onChange={() => handleProtectionTypeChange('both')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Both
                        </div>
                      </label>
                    </div>
                    {/* Add validation message if no protection type selected */}
                    {!protectionType && (
                      <p className="helper-text error" style={{ fontSize: '0.9rem', color: '#ef4444' }}>
                        Please select at least one protection type to continue
                      </p>
                    )}
                  </div>
                  
                  {/* Tie breaker preference section */}
                  <div className="form-group tiebreaker-group">
                    <label>How to break a tie <span className="required"></span></label>
                    <p className="helper-text" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                      If our results cause many cities to have the same exact laws, please let us know which is most important to you in breaking that tie (please choose only one):
                    </p>
                    <div className="tiebreaker-options">
                      {/* Most Resources option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleTieBreakerChange('resources');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="tiebreaker-resources"
                            name="tiebreaker"
                            type="radio"
                            checked={tieBreaker === 'resources'}
                            onChange={() => handleTieBreakerChange('resources')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Number of Resources in that city
                        </div>
                      </label>
                      
                      {/* Lowest Rental Costs option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleTieBreakerChange('rent');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="tiebreaker-rent"
                            name="tiebreaker"
                            type="radio"
                            checked={tieBreaker === 'rent'}
                            onChange={() => handleTieBreakerChange('rent')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Rental Costs
                        </div>
                      </label>
                      
                      {/* Closest Distance option */}
                      <label 
                        className="radio-checkbox-group"
                        onClick={(e) => {
                          if (e.target.type !== 'radio') {
                            handleTieBreakerChange('distance');
                          }
                        }}
                      >
                        <div className="radio-checkbox-container">
                          <input
                            id="tiebreaker-distance"
                            name="tiebreaker"
                            type="radio"
                            checked={tieBreaker === 'distance'}
                            onChange={() => handleTieBreakerChange('distance')}
                            className="radio-checkbox"
                          />
                        </div>
                        <div className="radio-checkbox-label">
                          Distance from where you live now
                        </div>
                      </label>
                    </div>
                    {/* Add validation message if no tie breaker selected */}
                    {!tieBreaker && protectionType && (
                      <p className="helper-text error" style={{ fontSize: '0.9rem', color: '#ef4444' }}>
                        Please select a tie-breaking preference to continue
                      </p>
                    )}
                  </div>
                  
                  {/* Submit button - only enabled when protection type and tie breaker are selected */}
                  <div className="button-container">
                    <button 
                      type="submit"
                      disabled={!protectionType || !tieBreaker || isTransitioning || isValidating}
                      className="submit-button"
                      style={{ 
                        opacity: protectionType && tieBreaker && !isTransitioning && !isValidating ? 1 : 0.6,
                        cursor: protectionType && tieBreaker && !isTransitioning && !isValidating ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {isValidating ? 'Validating...' : isTransitioning ? 'Loading...' : 'Continue'}
                    </button>
                  </div>
                  
                  {/* Terms and privacy links */}
                  <div className="terms-container" style={{ fontSize: '0.9rem' }}>
                  <p>
                    Your privacy is our priority. We do not collect, store, or share any personally identifiable information from our users.
                  </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchScreen;